<script setup>
import {onMounted, reactive, watch} from 'vue'
import {unique} from '@/paks/js-polyfill'

const emit = defineEmits(['click', 'update:modelValue'])

const props = defineProps({
    align: {type: String, default: 'left'},
    density: {type: String, enum: ['default', 'comfortable', 'compact'], default: 'compact'},
    items: Array,
    //  Should be itemTitle
    itemText: String,
    itemValue: String,
    label: String,
    loading: Boolean,
    chevron: {type: Boolean, default: false},
    width: {type: String, default: '40px'},
    modelValue: String,
})

const page = reactive({
    entries: [],
    left: true,
    menuItem: null,
    selected: null,
})

watch(() => props.modelValue, changeValue)
watch(() => props.label, changeLabel)
watch(() => props.items, prepEntries, {deep: true})

onMounted(() => {
    page.left = props.align == 'left'
    prepEntries(props.items)
})

function findSelected() {
    if (props.label) {
        page.selected = props.label
    } else {
        let entry = page.entries.find((e) => e.id == props.modelValue)
        if (entry) {
            page.selected = entry.text || entry.title
        }
    }
}

function clicked(entry) {
    page.selected = entry.text || entry.title
    //  So callers can listen on the v-model or when clicked if using a label
    emit('click', entry.id)
    emit('update:modelValue', entry.id)
}

function changeValue(value) {
    findSelected()
}

function changeLabel(label) {
    findSelected()
}

function prepEntries(items) {
    let list = []
    if (items) {
        // items = unique(items)
        if (!Array.isArray(items)) {
            items = items.split(/,/g)
        }
        let {itemText, itemValue} = props
        if (itemText && itemValue) {
            for (let item of items) {
                list.push({title: item[itemText], id: item[itemValue], disabled: item.disabled})
            }
        } else {
            for (let item of items) {
                if (typeof item == 'string' || typeof item == 'number') {
                    list.push({title: item, id: item})
                } else {
                    let title = item.label || item.name || item.title
                    list.push({title, id: item.id, disabled: item.disabled})
                }
            }
        }
        page.entries = list
        findSelected(props.modelValue)
    }
}
</script>

<!--
    vu-pick -- Selection list pick. Like v-select, but with a flat button instead of a text-entry field

    <vu-pick v-model="item" :items="items"
    <vu-pick v-model="item.id" :items="items" item-title="name" item-value="id"

    <vu-pick label="Selected text" :items="items" item-title="name" item-value="id"

    items = [1,2,3]
    items = [{name: 'text', id: 42}]

    Copyright (c) Embedthis. All Rights Reserved.
-->
<template>
    <div class="vu-pick">
        <v-menu
            v-model="page.menuItem"
            content-class="pick-items"
            nudge-right="0"
            nudge-bottom="1"
            offset-y
            transition="slide-y-transition"
            :left="page.left"
            :loading="loading"
            >
            <template v-slot:activator="{props}">
                <v-btn v-bind="props" variant="outlined" :min-width="width" :loading="loading">
                    {{ page.selected }}
                    <v-icon icon="$expand" v-if="chevron" />
                </v-btn>
            </template>
            <v-list flat :min-width="width">
                <v-list-item
                    v-for="entry in page.entries"
                    :key="entry.id"
                    :disabled="entry.disabled"
                    @click="clicked(entry)">
                    {{ entry.title }}
                </v-list-item>
            </v-list>
        </v-menu>
    </div>
</template>

<style lang="scss" scoped>
.v-btn {
    padding: 10px;
    margin: 0 !important;
    min-width: 32px;
    border: 1px solid rgb(var(--v-theme-border));
    background: none !important;
    box-shadow: none !important;
}
.v-btn__content i {
    color: rgb(var(--v-theme-text-lighten-1)) !important;
}
.pick-items {
    font-size: 1rem;
    background: rgv(var(--v-theme-background));
    .v-list {
        padding: 8px 0 8px 0;
        border: solid 1px rgb(var(--v-theme-border));
    }
}
.v-list-item {
    min-height: 30px !important;
}
.v-list-item__content {
    padding: 6px 0 6px 0 !important;
}
.v-list {
    padding: 0;
}
</style>
